import type {
  AIDecompositionResult, Comment, DiffResult, GraphEdge, GraphNode, NodeVariant,
  ReactionType, Strain, StrainGraph, User, VariantIntent,
} from "@/types";

// the store registers a handler so an expired session cookie clears the
// persisted "logged in" UI state instead of leaving it stale
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...options,
    credentials: "include",
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers ?? {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/api/auth/")) {
      onUnauthorized?.();
    }
    throw new Error(data?.error ?? `Request failed: ${res.status}`);
  }
  return data as T;
}

export const apiClient = {
  getStrains: (filter = "new") => api<Strain[]>(`/api/strains?filter=${encodeURIComponent(filter)}`),
  getGraphs: (slugs?: string[]) =>
    api<StrainGraph[]>(`/api/graphs${slugs?.length ? `?slugs=${encodeURIComponent(slugs.join(","))}` : ""}`),
  getGraphBySlug: (slug: string) => api<StrainGraph>(`/api/strains/${encodeURIComponent(slug)}/graph`),
  getGraphByVersion: (versionId: string) => api<StrainGraph>(`/api/versions/${encodeURIComponent(versionId)}/graph`),
  getCruxes: (versionId: string) => api(`/api/versions/${encodeURIComponent(versionId)}/cruxes`),
  getLineage: (versionId: string) => api(`/api/versions/${encodeURIComponent(versionId)}/lineage`),
  getDiff: (leftId: string, rightId: string) => api<DiffResult>(`/api/versions/${encodeURIComponent(leftId)}/diff/${encodeURIComponent(rightId)}`),

  signup: (body: { username: string; email: string; password: string; display_name?: string }) =>
    api<User>("/api/auth/signup", { method: "POST", body: JSON.stringify(body) }),
  login: (body: { username: string; password: string }) =>
    api<User>("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
  logout: () => api<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  me: () => api<User>("/api/auth/me"),

  getUsers: () => api<User[]>("/api/users"),
  getUser: (username: string) => api<User>(`/api/users/${encodeURIComponent(username)}`),

  createStrain: (body: {
    slug?: string;
    title: string;
    summary: string;
    original_text?: string;
    tags: string[];
    visibility: "public" | "unlisted" | "private";
    nodes: AIDecompositionResult["nodes"];
    edges: AIDecompositionResult["edges"];
    variants?: AIDecompositionResult["thesis_variants"];
  }) => api<StrainGraph>("/api/strains", { method: "POST", body: JSON.stringify(body) }),

  forkVersion: (versionId: string, body: Record<string, unknown>) =>
    api<StrainGraph>(`/api/versions/${encodeURIComponent(versionId)}/fork`, { method: "POST", body: JSON.stringify(body) }),

  addNode: (versionId: string, body: Omit<GraphNode, "id">) =>
    api<GraphNode>(`/api/versions/${encodeURIComponent(versionId)}/nodes`, { method: "POST", body: JSON.stringify(body) }),
  updateNode: (nodeId: string, versionId: string, patch: Partial<GraphNode>) =>
    api<GraphNode>(`/api/nodes/${encodeURIComponent(nodeId)}`, { method: "PATCH", body: JSON.stringify({ ...patch, version_id: versionId }) }),
  deleteNode: (nodeId: string, versionId: string) =>
    api<{ ok: boolean }>(`/api/nodes/${encodeURIComponent(nodeId)}?version_id=${encodeURIComponent(versionId)}`, { method: "DELETE" }),

  addVariant: (nodeId: string, versionId: string, body: { intent: VariantIntent; text: string; note?: string }) =>
    api<NodeVariant>(`/api/nodes/${encodeURIComponent(nodeId)}/variants`, {
      method: "POST",
      body: JSON.stringify({ ...body, version_id: versionId }),
    }),
  deleteVariant: (variantId: string) =>
    api<{ ok: boolean }>(`/api/variants/${encodeURIComponent(variantId)}`, { method: "DELETE" }),

  addEdge: (versionId: string, body: Omit<GraphEdge, "id">) =>
    api<GraphEdge>(`/api/versions/${encodeURIComponent(versionId)}/edges`, { method: "POST", body: JSON.stringify(body) }),
  updateEdge: (edgeId: string, versionId: string, patch: Partial<GraphEdge>) =>
    api<GraphEdge>(`/api/edges/${encodeURIComponent(edgeId)}`, { method: "PATCH", body: JSON.stringify({ ...patch, version_id: versionId }) }),
  deleteEdge: (edgeId: string, versionId: string) =>
    api<{ ok: boolean }>(`/api/edges/${encodeURIComponent(edgeId)}?version_id=${encodeURIComponent(versionId)}`, { method: "DELETE" }),

  getComments: (strainId: string) => api<Comment[]>(`/api/strains/${encodeURIComponent(strainId)}/comments?anchored=1`),
  addComment: (body: Omit<Comment, "id" | "created_at" | "status" | "author" | "author_avatar">) =>
    api<Comment>("/api/comments", { method: "POST", body: JSON.stringify(body) }),

  toggleReaction: (body: { target_type: "strain" | "node" | "edge" | "version" | "node_variant"; target_id: string; reaction_type: ReactionType | "prefer_wording" }) =>
    api<{ removed: boolean }>("/api/reactions", { method: "POST", body: JSON.stringify(body) }),
  setStance: (body: { strain_id: string; version_id: string; before_score: number; after_score: number; note?: string; moved_by_node_id?: string | null }) =>
    api("/api/stance", { method: "POST", body: JSON.stringify(body) }),

  search: (q: string) => api(`/api/search?q=${encodeURIComponent(q)}`),
  adminReports: () => api("/api/admin/reports"),
  adminUsers: () => api<User[]>("/api/admin/users"),
  adminStrains: () => api<Strain[]>("/api/admin/strains"),
  hideStrain: (id: string) => api<Strain>(`/api/admin/strains/${encodeURIComponent(id)}/hide`, { method: "POST" }),
};
